import { create } from 'zustand';
import type { Role } from '../auth/roles';

export interface AuthUser {
  sub: string;
  role: Role;
  customerId?: string | null;
  email?: string;
  /** Set from the token's mustChangePassword claim — drives the forced first-login redirect. */
  mustChangePassword?: boolean;
}

interface AuthStore {
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  setSession: (token: string, user: AuthUser) => void;
  logout: () => void;
}

// JWT payload decoded ONLY for display ("Signed in as …"). Authorisation is
// never inferred from these claims on the frontend — see capabilitiesStore.
function decodeFromToken(token: string): AuthUser | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return {
      sub: json.sub,
      role: json.role,
      customerId: json.customerId,
      email: json.email,
      mustChangePassword: json.mustChangePassword === true,
    };
  } catch {
    return null;
  }
}

const persisted = (() => {
  const token = localStorage.getItem('auth_token');
  if (!token) return { token: null as string | null, user: null as AuthUser | null };
  return { token, user: decodeFromToken(token) };
})();

export const useAuthStore = create<AuthStore>((set) => ({
  isAuthenticated: !!persisted.token,
  user: persisted.user,
  token: persisted.token,
  setSession: (token, user) => {
    localStorage.setItem('auth_token', token);
    set({ isAuthenticated: true, token, user });
  },
  logout: () => {
    localStorage.removeItem('auth_token');
    // Must go too, now that the API client silently rotates on 401: a refresh token left
    // behind would let the next stray request mint a fresh session for a signed-out user.
    localStorage.removeItem('refresh_token');
    set({ isAuthenticated: false, token: null, user: null });
  },
}));
